import {
    extendTheme,
    theme as baseTheme,
    withDefaultColorScheme,
} from '@chakra-ui/react';
import { customColors, config, styles, activeLabelStyles } from './constants';
import { APP_SETTINGS, DEFAULT_COLOR } from 'context/LayoutContext';
import store from './store/store';
import { getBrandColor } from 'utils/getBrandColor';

function getColorScheme() {
    return store.getState().settings?.colorScheme;
}

// Chakra's color mode follows the app's darkMode setting, but its built-in
// component styles should keep their light-mode look (dark mode is applied
// explicitly per component via darkMode / `_dark`). Re-run every default
// component style function as if colorMode were 'light'.
//
// Passed as the LAST argument to extendTheme below, which is Chakra's
// documented escape hatch for supplying a replacement *base* theme (see the
// extendTheme JSDoc) — everything else still merges on top of it.
const forceLightMode = (style) =>
    typeof style === 'function'
        ? (props) => style({ ...props, colorMode: 'light' })
        : style;

const forceLightModeMap = (styles) =>
    styles &&
    Object.fromEntries(
        Object.entries(styles).map(([key, style]) => [
            key,
            forceLightMode(style),
        ]),
    );

const lightModeBaseTheme = {
    ...baseTheme,
    // Chakra's *global* styles switch on colorMode too: `body` bg/colour,
    // `*::placeholder` and the default `*, *::before, &::after` border colour.
    // Leaving them mode-aware repainted every page background (gray.800
    // instead of white) and shifted default borders/placeholders in dark mode,
    // so pin them light as well — dark.css/light.css still own the real page
    // surface via `body.chakra-ui-dark`/`body.chakra-ui-light !important`.
    styles: {
        ...baseTheme.styles,
        global: forceLightMode(baseTheme.styles.global),
    },
    components: Object.fromEntries(
        Object.entries(baseTheme.components).map(([name, component]) => [
            name,
            {
                ...component,
                baseStyle: forceLightMode(component.baseStyle),
                variants: forceLightModeMap(component.variants),
                sizes: forceLightModeMap(component.sizes),
            },
        ]),
    ),
};

// Focus ring colour is pinned to brand.500 regardless of light/dark mode —
// the same "always look right" treatment the rest of this file gives
// built-in Chakra styles, rather than falling back to a paler shade in dark
// mode.
/** @type import('@chakra-ui/styled-system'.StyleProps)*/
const Input = {
    variants: {
        outline: {
            field: {
                background: '#FFF',
                _focus: {
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                },
            },
        },
    },
};
const Select = {
    variants: {
        outline: {
            field: {
                background: '#FFF',
                _focus: {
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                },
            },
        },
    },
};

// YIQ brightness, used to pick readable button text over an arbitrary brand
// colour (some brand palettes' .500 step is pale enough that white text no
// longer reads well against it). A plain WCAG-max-contrast pick is too
// aggressive here — it flips this app's own default brand blue (#007BFF) to
// black, which reads worse in practice than the YIQ >= 128 threshold below.
function readableTextColor(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
    if (!match) return 'white';
    const [r, g, b] = [0, 2, 4].map((i) =>
        parseInt(match[1].slice(i, i + 2), 16),
    );
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? 'black' : 'white';
}

const Button = {
    baseStyle: {
        borderRadius: '0.25rem',
    },
    variants: {
        // Brand/custom palettes: colorScheme.500 background with white text,
        // falling back to black text when the colour is too pale for white
        // to read clearly. Chakra's built-in palettes (red, orange, gray, ...)
        // keep Chakra's default solid styling.
        solid: (props) => {
            const { colorScheme: c, theme } = props;
            if (c in baseTheme.colors) return {};
            const bgHex = theme.colors?.[c]?.[500];
            return {
                bg: `${c}.500`,
                color: readableTextColor(bgHex),
            };
        },
    },
};

const Form = {
    variants: {
        floatingSelect: {
            container: {
                _focusWithin: {
                    label: {
                        ...activeLabelStyles,
                    },
                },
                'input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, .react-date-picker + label, .chakra-react-select--has-value + label':
                    {
                        ...activeLabelStyles,
                    },
                label: {
                    top: 0,
                    left: 0,
                    zIndex: 2,
                    position: 'absolute',
                    backgroundColor: 'white',
                    pointerEvents: 'none',
                    mx: 3,
                    px: 1,
                    my: 2,
                    transformOrigin: 'left top',
                },
            },
        },
    },
};

const FormLabel = {
    variants: {
        floating: {
            fontSize: 'sm',
            position: 'absolute',
            transition: 'all 0.2s ease-in 0s',
            top: '-27px',
            left: 0,
        },
        inline: {
            margin: '0px',
        },
    },
};

const Card = {
    variants: {
        rounded: {
            padding: 8,
            borderRadius: 'xl',
            boxShadow: 'xl',
        },
        smooth: {
            padding: 6,
            borderRadius: 'base',
            boxShadow: 'md',
        },
    },
};

const Container = {
    baseStyle: {
        maxWidth: 'none',
    },
};

const Tabs = {
    baseStyle: {
        tab: {
            // minW: '160px',
            // _focus: {
            //     boxShadow: 'none',
            // },
            // _selected: {
            //     borderBottom: '2px solid #03a9f4!important',
            //     transition:
            //         'border-bottom 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            //     opacity: '1 !important',
            // },
            // _active: {
            //     position: 'relative',
            //     overflow: 'hidden',
            //     _before: {
            //         content: '""',
            //         position: 'absolute',
            //         backgroundColor: 'rgba(3, 169, 244, 0.5)',
            //         top: '50%',
            //         left: '50%',
            //         width: '120%',
            //         height: '120%',
            //         transform: 'translate(-50%, -50%)',
            //         borderRadius: '50%',
            //         animation: 'ripple .5s linear infinite',
            //     },
            //     '@keyframes ripple': {
            //         '0%': {
            //             width: 0,
            //             height: 0,
            //             opacity: 0.5,
            //         },
            //         '100%': {
            //             width: '500px',
            //             height: '500px',
            //             opacity: 0,
            //         },
            //     },
            // },
            _dark: {
                textColor: 'white',
            },
            _light: {
                textColor: '#262626',
            },
        },
        // root: {
        //     _focus: {
        //         boxShadow: 'none',
        //     },
        //     fontFamily: 'Inter, Helvetica, Arial',
        //     fontWeight: '500',
        // },
        // tablist: {
        //     border: 'none !important',
        // },
    },
    defaultProps: {
        colorScheme: 'brand',
    },
};

const globalColor = JSON.parse(
    localStorage.getItem(APP_SETTINGS) ||
        `{ "colorScheme": "${DEFAULT_COLOR}" }`,
).colorScheme;
const brandColor = getBrandColor(globalColor);

export default extendTheme(
    {
        fonts: {
            heading:
                "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            mono: "'Inter', source-code-pro, Menlo, Monaco, Consolas, monospace",
        },
        colors: {
            brand: brandColor,
            ...customColors,
        },
        shadows: {
            outline: '0 0 0 1px var(--chakra-colors-brand-500)',
        },
        components: {
            Input,
            Select,
            Form,
            FormLabel,
            Button,
            Card,
            Container,
            Tabs,
        },
        styles,
        config,
    },
    withDefaultColorScheme({
        colorScheme: 'brand',
        commponents: ['Input', 'Select', 'Tabs'],
    }),
    withDefaultColorScheme({
        colorScheme: 'gray',
        components: ['Progress'],
    }),
    lightModeBaseTheme,
);

export const tcapDarkShadow = '0 0 0 1px #6a696a';

export const ChakraDatePickerDarkModeStyles = {
    dateNavBtnProps: {
        colorScheme: 'brand',
        color: 'brand.400',
        variant: 'outline',
        _hover: {
            background: 'brand.200',
        },
    },
    inputProps: {
        borderColor: 'dark.tcap-borderGray',
    },
    dayOfMonthBtnProps: {
        defaultBtnProps: {
            borderRadius: '50%',
            width: '2rem',
            _hover: {
                background: 'brand.400',
            },
        },
        isInRangeBtnProps: {
            color: 'brand.800',
            background: 'brand.200',
            borderColor: 'blue.300',
            opacity: '0.9',
        },
        selectedBtnProps: {
            background: 'brand.400',
            borderColor: 'blue.300',
        },
    },
    popoverCompProps: {
        popoverContentProps: {
            background: 'dark.bgDarkGray',
            color: 'white',
        },
    },
};

export const ChakraDatePickerStyles = {
    dateNavBtnProps: {
        colorScheme: 'brand',
        variant: 'outline',
    },
    dayOfMonthBtnProps: {
        defaultBtnProps: {
            borderRadius: '50%',
            width: '2rem',
            _hover: {
                background: 'brand.400',
            },
        },
        isInRangeBtnProps: {
            color: 'brand.800',
            background: 'brand.200',
            borderColor: 'blue.300',
            opacity: '0.9',
        },
        selectedBtnProps: {
            background: 'brand.400',
            borderColor: 'blue.300',
        },
    },
};

export const floatingLabelStyles = (darkMode) => ({
    '[data-peer="true"]:placeholder-shown ~ label': {
        fontSize: 'md',
        top: 2,
        left: 4,
        fontWeight: 'normal',
        color: darkMode ? 'gray.500' : 'gray.400',
    },
    '[data-peer="true"]:focus ~ label': {
        fontSize: 'sm',
        fontWeight: 'medium',
        top: '-27px',
        left: 0,
    },
    '[data-peer="true"] ~ label': {
        color: darkMode ? 'white' : 'black',
    },
});

export const floatingSelectStyles = (darkMode) => {
    const colorScheme = getColorScheme();
    return {
        'label.chakra-form__label': {
            bg: darkMode ? 'dark.bgDark' : '#F5F5F5',
            color: darkMode
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.54)',
            fontWeight: '400',
            letterSpacing: '0.00938em',
            '&[data-focus]': {
                color: `${colorScheme}.500`,
            },
        },
        'div.chakra-react-select__control': {
            bg: darkMode ? 'dark.bgDark' : '#F5F5F5',
        },
    };
};

export const dateInputGroupStyle = {
    maxWidth: '20ch',
    '@media (max-width: 768px)': {
        maxWidth: '5ch',
    },
    '& input': {
        '@media (max-width: 768px)': {
            color: 'transparent',
        },
    },

    '& div.chakra-input__right-element': {
        'media (max-width: 768px)': {
            marginLeft: 0,
        },
    },
};
export const disabledSelectedDateStyle = {
    'button[aria-pressed="true"]': {
        background: 'brand.400',
    },
};

export const getDayofMonthBtnProps = (darkMode) => ({
    defaultBtnProps: {
        borderRadius: '50%',
        width: '2rem',
        _hover: {
            background: 'brand.400',
        },
        _active: {
            color: 'brand.500',
            background: 'brand.100',
        },
        color: darkMode ? 'dark.textDark' : 'black',
    },
    selectedBtnProps: {
        background: 'brand.400',

        _hover: {
            background: 'brand.300',
        },
    },
});

// Shared "chip input" look — used by components/v4/ChipInput's Chip and by
// components/presentational/ChipInsertPalette (via chakra-react-select's
// chakraStyles.multiValue/multiValueLabel). The close button's dark-mode
// background is a custom shade with no exact gray-token match, kept as a
// literal hex.
export const chipTagStyles = (darkMode) => ({
    bg: darkMode ? 'gray.600' : 'gray.100',
    color: darkMode ? 'white' : 'gray.700',
});

export const chipTagCloseButtonStyles = (darkMode) => ({
    bg: darkMode ? '#374155' : 'gray.500',
    color: darkMode ? 'gray.400' : 'white',
    boxSize: 4,
});

export const chipTagCloseButtonSxStyles = {
    svg: {
        boxSize: '13px',
    },
};
